import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { OpenFullProfileLink } from "@components/candidates/OpenFullProfileLink";
import { ChevronDown, ChevronRight, ExternalLink, FileText, FileX } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { StageBadge } from "./StageBadge";

interface CandidateResume {
  id: string;
  filename: string;
  url: string;
  byteSize: number;
  createdAt: string;
}

interface CandidateRowProps {
  candidate: {
    id: string;
    referenceNumber?: number | null;
    fullName: string;
    email?: string | null;
    currentRoleTitle?: string | null;
    currentCompany?: string | null;
    location?: string | null;
    linkedinUrl?: string | null;
    resumes?: CandidateResume[] | null;
  };
  source?: string | null;
  addedBy: { name: string };
  createdAt: string;
  actions?: React.ReactNode;
  /** Optional badges (e.g. pool memberships) rendered below the candidate name */
  badges?: React.ReactNode;
  /** Optional inline badges (e.g. assessment score) rendered beside the actions */
  assessmentBadge?: React.ReactNode;
  /** Optional block rendered at the top of the expanded area (e.g. assessment summary) */
  assessmentContent?: React.ReactNode;
  /** The candidate's actual current stage */
  currentStage?: string;
  /** Whether this candidate has moved past the stage being viewed */
  movedPast?: boolean;
  /** Explicit status chip (e.g. the roster's "In pool"); rendered where the moved-past stage badge goes */
  statusBadge?: React.ReactNode;
  /** When provided, renders a leading checkbox for bulk selection */
  onToggleSelect?: () => void;
  /** Reflects whether this row is currently selected (only rendered when onToggleSelect is set) */
  selected?: boolean;
  /** When both are provided, the candidate name links to the campaign-scoped detail route */
  campaignId?: string;
  campaignCandidateId?: string;
}

export function CandidateRow({
  candidate,
  source,
  addedBy,
  createdAt,
  actions,
  badges,
  assessmentBadge,
  assessmentContent,
  currentStage,
  movedPast,
  statusBadge,
  onToggleSelect,
  selected,
  campaignId,
  campaignCandidateId,
}: CandidateRowProps) {
  const [expanded, setExpanded] = useState(false);
  const detailHref =
    campaignId && campaignCandidateId
      ? `/campaigns/${campaignId}/candidates/${campaignCandidateId}`
      : null;

  return (
    <div className="border-b border-border">
      <div className="flex items-center gap-3 px-3 py-2">
        {onToggleSelect && (
          <input
            type="checkbox"
            aria-label={`Select ${candidate.fullName}`}
            checked={!!selected}
            onChange={onToggleSelect}
            className="h-4 w-4 cursor-pointer rounded border-border accent-accent"
          />
        )}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-label={expanded ? `Collapse ${candidate.fullName}` : `Expand ${candidate.fullName}`}
          className="rounded p-0.5 text-text-faint hover:bg-card-hover"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div className="min-w-0 flex-1">
          <div>
            {typeof candidate.referenceNumber === "number" && (
              <>
                <CandidateReferenceBadge referenceNumber={candidate.referenceNumber} />{" "}
              </>
            )}
            {detailHref ? (
              <Link
                to={detailHref}
                className="text-sm font-medium text-text-primary hover:text-accent hover:underline"
              >
                {candidate.fullName}
              </Link>
            ) : (
              <span className="text-sm font-medium text-text-primary">{candidate.fullName}</span>
            )}
            {(candidate.currentRoleTitle || candidate.currentCompany) && (
              <span className="ml-2 text-xs text-text-muted">
                {[candidate.currentRoleTitle, candidate.currentCompany]
                  .filter(Boolean)
                  .join(" at ")}
              </span>
            )}
            <span className="ml-3">
              <OpenFullProfileLink candidateId={candidate.id} />
            </span>
          </div>
          {badges}
        </div>
        {candidate.location && (
          <span className="text-xs text-text-faint">{candidate.location}</span>
        )}
        <CvBadge resumes={candidate.resumes} />
        {assessmentBadge}
        {statusBadge}
        {movedPast && currentStage && <StageBadge stage={currentStage} />}
        {source && !movedPast && (
          <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            {source}
          </span>
        )}
        {actions}
      </div>
      {expanded && (
        <div className="ml-10 space-y-3 pb-3">
          {assessmentContent && <div className="pr-3">{assessmentContent}</div>}
          <div className="grid grid-cols-3 gap-3 text-xs">
            {candidate.email && (
              <div>
                <span className="text-text-muted">Email:</span>{" "}
                <span className="text-text-secondary">{candidate.email}</span>
              </div>
            )}
            {candidate.linkedinUrl && (
              <div>
                <a
                  href={candidate.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:text-accent/80"
                >
                  LinkedIn <ExternalLink size={10} />
                </a>
              </div>
            )}
            <div>
              <span className="text-text-muted">Added by:</span>{" "}
              <span className="text-text-secondary">{addedBy.name}</span>
            </div>
            <div>
              <span className="text-text-muted">Added:</span>{" "}
              <span className="text-text-secondary">
                {new Date(createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
          {candidate.resumes && candidate.resumes.length > 0 && (
            <div className="pr-3">
              <div className="text-xs font-medium text-text-muted">CVs</div>
              <ul className="mt-1 space-y-1">
                {candidate.resumes.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 text-xs">
                    <FileText size={12} className="text-text-faint" />
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-accent hover:underline"
                    >
                      {r.filename} <ExternalLink size={10} />
                    </a>
                    <span className="text-text-faint">
                      {Math.max(1, Math.round(r.byteSize / 1024))} KB · uploaded{" "}
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CvBadge({ resumes }: { resumes?: CandidateResume[] | null }) {
  if (!resumes || resumes.length === 0) {
    return (
      <span
        title="No CV on record"
        className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-500"
      >
        <FileX size={10} /> No CV
      </span>
    );
  }
  const latest = resumes[0];
  const extra = resumes.length > 1 ? ` (+${resumes.length - 1})` : "";
  return (
    <a
      href={latest.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`Open ${latest.filename} in a new tab`}
      className="inline-flex max-w-[160px] items-center gap-1 truncate rounded bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium text-accent hover:bg-accent/15"
    >
      <FileText size={10} className="shrink-0" />
      <span className="truncate">CV{extra}</span>
    </a>
  );
}
